import { Button, Checkbox, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";
import { Box } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";

interface ListPageHeaderBaseProps {
    title: string
    search: string
    onSearchChange: (v: string) => void
    //searchLabel overrides the label that is otherwise derived from
    //searchAllFields
    searchLabel?: string
    actionId?: string
    actionLabel?: string
    onAction?: () => void
    //children render between the checkbox and the action button
    children?: React.ReactNode
}

//searchAllFields being undefined hides the checkbox entirely. When it is given
//the checkbox is rendered, and a rendered checkbox that cannot report a change
//is stuck on whatever it started as, so the handler comes with it.
type SearchAllFieldsProps = {
    searchAllFields: boolean
    onSearchAllFieldsChange: (v: boolean) => void
} | {
    searchAllFields?: undefined
    onSearchAllFieldsChange?: undefined
};

type ListPageHeaderProps = ListPageHeaderBaseProps & SearchAllFieldsProps;

//searchLabelFor tells the user what the field actually searches: with the
//checkbox off the API only matches names.
function searchLabelFor(searchAllFields: boolean | undefined) {
    if (searchAllFields === undefined || searchAllFields) {
        return "Search";
    }

    return "Search by Name";
}

//ListPageHeader is the title row every list page opens with: heading, search
//field, an optional "search all fields" toggle and an optional add button.
export function ListPageHeader(props: ListPageHeaderProps) {
    const label = props.searchLabel || searchLabelFor(props.searchAllFields);

    return <Box display="flex">
        <Typography variant="h1">{props.title}</Typography>

        <FormControl variant="outlined" sx={{ marginLeft: "auto" }}>
            <InputLabel htmlFor="search-input">{label}</InputLabel>
            <OutlinedInput
                id="search-input"
                label={label}
                value={props.search}
                onChange={x => props.onSearchChange(x.target.value)}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton aria-label="Clear search" size="small" onClick={() => props.onSearchChange("")}>
                            <ClearIcon />
                        </IconButton>
                    </InputAdornment>
                }
            />
        </FormControl>

        {props.searchAllFields !== undefined &&
            <FormControlLabel
                sx={{ marginLeft: "5px" }}
                control={<Checkbox
                    checked={props.searchAllFields}
                    onChange={x => props.onSearchAllFieldsChange(x.target.checked)}
                />}
                label="Search all fields"
            />
        }

        {props.children}

        {props.onAction &&
            <Button
                id={props.actionId}
                sx={{ marginLeft: "5px" }}
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={props.onAction}
            >
                {props.actionLabel}
            </Button>
        }
    </Box>;
}

export default ListPageHeader;
